import { WeightValueEntry, WeightValues } from "@/types/dataset";
import {
  getIsIncludedScoring,
  parseWeightValue,
  WeightPrimitive
} from "@/utils/weightValues";

export const calculatePercentagesWithRounding = <T>(
  entries: [string, T][],
  getWeight: (value: T) => number,
  totalWeight: number,
  targetSum: number
): Record<string, string> => {
  if (entries.length === 0) {
    return {};
  }

  // When totalWeight is 0, distribute equally using largest remainder method
  const effectiveTotalWeight = totalWeight === 0 ? entries.length : totalWeight;
  const getEffectiveWeight = (value: T) =>
    totalWeight === 0 ? 1 : getWeight(value);

  // Calculate raw percentages (2 decimal precision for target)
  const rawPercentages = entries.map(([id, value]) => {
    const pct = (getEffectiveWeight(value) / effectiveTotalWeight) * targetSum;
    return { id, pct };
  });

  // Floor all values to 2 decimal places and track remainders
  const flooredData = rawPercentages.map(({ id, pct }) => {
    const floored = Math.floor(pct * 100) / 100;
    const remainder = pct * 100 - Math.floor(pct * 100);
    return { id, floored, remainder };
  });

  // Calculate how much we need to distribute (in cents)
  const flooredSum = flooredData.reduce((sum, { floored }) => sum + floored, 0);
  const targetSumCents = Math.round(targetSum * 100);
  const flooredSumCents = Math.round(flooredSum * 100);
  let centsToDistribute = targetSumCents - flooredSumCents;

  // Sort by remainder descending to distribute cents to items with largest remainders
  const sortedByRemainder = [...flooredData].sort(
    (a, b) => b.remainder - a.remainder
  );

  // Distribute extra cents to items with largest remainders
  const adjustments: Record<string, number> = {};
  for (const { id } of sortedByRemainder) {
    if (centsToDistribute > 0) {
      adjustments[id] = 0.01;
      centsToDistribute--;
    } else {
      adjustments[id] = 0;
    }
  }

  // Build result with adjusted values
  const result: Record<string, string> = {};
  for (const { id, floored } of flooredData) {
    const finalValue = floored + (adjustments[id] || 0);
    result[id] = finalValue
      .toFixed(2)
      .replace(/\.00$/, "")
      .replace(/(\.\d)0$/, "$1");
  }

  return result;
};

export const initializeDatasetWeights = (
  weightValues: WeightValues
): WeightValues => {
  return Object.fromEntries(
    Object.entries(weightValues).map(([datasetId, dataset]) => [
      datasetId,
      {
        weight: dataset.weight,
        ...(dataset.include_scoring !== undefined
          ? { include_scoring: dataset.include_scoring }
          : {}),
        attributes: Object.fromEntries(
          Object.entries(dataset.attributes || {}).map(([attrId, entry]) => [
            attrId,
            { ...entry }
          ])
        )
      }
    ])
  );
};

export const calculateAttributeGlobalPercentages = (
  datasetAttributesWeights: Record<string, WeightValueEntry>,
  datasetWeightPercentage: string
): Record<string, string> => {
  // Parent dataset excluded → every attribute is 0.
  if (parseWeightValue(datasetWeightPercentage) === 0) {
    return Object.fromEntries(
      Object.keys(datasetAttributesWeights).map((id) => [id, "0"])
    );
  }

  const enabledEntries = Object.entries(datasetAttributesWeights).filter(
    ([, entry]) => getIsIncludedScoring(entry)
  );
  const totalLocalAttributeWeight = enabledEntries.reduce(
    (sum, [, weight]) => sum + parseWeightValue(weight),
    0
  );
  const rounded = calculatePercentagesWithRounding(
    enabledEntries,
    parseWeightValue,
    totalLocalAttributeWeight,
    parseWeightValue(datasetWeightPercentage)
  );

  return Object.fromEntries(
    Object.keys(datasetAttributesWeights).map((id) => [
      id,
      getIsIncludedScoring(datasetAttributesWeights[id])
        ? (rounded[id] ?? "0")
        : "0"
    ])
  );
};

export const calculateDatasetWeightPercentages = (
  weightValues: WeightValues
): Record<string, string> => {
  const enabledEntries = Object.entries(weightValues).filter(
    ([, dataset]) => dataset.include_scoring !== false
  );
  const totalWeight = enabledEntries.reduce(
    (sum, [, dataset]) => sum + parseWeightValue(dataset.weight),
    0
  );

  const rounded = calculatePercentagesWithRounding(
    enabledEntries,
    (dataset) => parseWeightValue(dataset.weight),
    totalWeight,
    100
  );

  return Object.fromEntries(
    Object.keys(weightValues).map((id) => [
      id,
      weightValues[id].include_scoring === false
        ? "0"
        : (rounded[id] ?? "0")
    ])
  );
};

export const calculateWeightPercentage = (
  attributeId: string,
  datasetTotalWeight: number,
  datasetAttributeWeights: Record<string, WeightPrimitive>
): string => {
  if (!datasetTotalWeight || datasetTotalWeight <= 0) {
    return "0%";
  }

  const attributeWeight = datasetAttributeWeights?.[attributeId];
  const parsedWeight = parseWeightValue(attributeWeight);

  if (parsedWeight <= 0) {
    return "0%";
  }

  const percentage = (parsedWeight / datasetTotalWeight) * 100;
  const formattedPercentage = percentage
    .toFixed(2)
    .replace(/\.00$/, "")
    .replace(/(\.\d)0$/, "$1");

  return `${formattedPercentage}%`;
};
